import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  ToggleButtonGroup, ToggleButton, Alert, Typography, Box,
} from '@mui/material';
import { ALL_SERVICE_CATEGORIES_QUERY } from '../graphql/queries';

const EMPTY = { source: 'service', serviceCategory: '', name: '', plannedAmount: '', description: '', color: '#4a90e2' };

// New/edit budget category. A new one starts from a wedding service (one budget per service)
// or is a custom category; the budget amount is what spending is monitored against.
export default function BudgetCategoryDialog({ open, editing, existingServices = [], familyName, error, busy, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const { data } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: true }, skip: !open });
  const services = (data?.allServiceCategories || []).filter(c => !['matchmaking', 'other'].includes(c.name));
  const available = services.filter(c => !existingServices.includes(c.name));

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { source: editing.serviceCategory ? 'service' : 'custom', serviceCategory: editing.serviceCategory || '', name: editing.name,
        plannedAmount: editing.plannedAmount ? String(editing.plannedAmount) : '', description: editing.description || '', color: editing.color || '#4a90e2' }
      : EMPTY);
  }, [open, editing]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const canSave = editing ? form.name.trim() : (form.source === 'service' ? form.serviceCategory : form.name.trim());

  const save = () => {
    const vars = {
      description: form.description || null,
      plannedAmount: Number(form.plannedAmount) || 0,
      color: form.color || null,
    };
    if (editing) onSave({ ...vars, name: form.name.trim() });
    else if (form.source === 'service') onSave({ ...vars, serviceCategory: form.serviceCategory });
    else onSave({ ...vars, name: form.name.trim() });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {editing ? `Edit budget — ${editing.name}` : 'New budget category'}
        {familyName && <Typography variant="caption" display="block" color="text.secondary">For {familyName}</Typography>}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {!editing && (
            <ToggleButtonGroup exclusive size="small" fullWidth value={form.source} onChange={(_, v) => v && setForm(f => ({ ...f, source: v }))}>
              <ToggleButton value="service">For a service</ToggleButton>
              <ToggleButton value="custom">Custom</ToggleButton>
            </ToggleButtonGroup>
          )}
          {!editing && form.source === 'service' && (
            <TextField select label="Service" value={form.serviceCategory} onChange={set('serviceCategory')} fullWidth required
              helperText="Budgets for the services already selected are started automatically.">
              {available.length === 0 && <MenuItem value="" disabled><em>Every service already has a budget</em></MenuItem>}
              {available.map(c => <MenuItem key={c.name} value={c.name}>{c.displayName}</MenuItem>)}
            </TextField>
          )}
          {(editing || form.source === 'custom') && (
            <TextField label="Category name" value={form.name} onChange={set('name')} fullWidth required
              disabled={Boolean(editing?.serviceCategory)} helperText={editing?.serviceCategory ? 'Named after its service' : ''} />
          )}
          <TextField label="Budget amount (₹)" type="number" value={form.plannedAmount} onChange={set('plannedAmount')} fullWidth
            inputProps={{ min: 0 }}
            helperText="Spending is monitored against this: an alert is sent at 80% and when it is exceeded. Left empty, the items' planned amounts are used." />
          <TextField label="Description" value={form.description} onChange={set('description')} fullWidth multiline rows={2} />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body2">Colour</Typography>
            <Box component="input" type="color" value={form.color} onChange={set('color')}
              sx={{ width: 48, height: 32, border: 'none', cursor: 'pointer', borderRadius: 1, bgcolor: 'transparent' }} />
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!canSave || busy}>{editing ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
